import { Tag } from "@stenajs-webui/elements";
import { StandardTableConfig } from "@stenajs-webui/grid";
import { Indent, Text } from "@stenajs-webui/core";
import { DeleteParentButton } from "../components/DeleteParentButton";
import { ParentHistoryEndDateCell } from "../components/ParentHistoryEndDateCell";
import {
  parentStretchState,
  parentStretchTag,
} from "../lib/parentStretchState";
import { ParentStretchKey } from "../hooks/UseParentMutations";
import { ParentHistoryEntry } from "../types";

/** How the accounts API addresses the stretch a write is aimed at. */
const stretchKey = (entry: ParentHistoryEntry): ParentStretchKey => ({
  parentCustomerNo: Number(entry.parentCustomerNo),
  validFrom: entry.validFrom,
});

type HistoryColumns = "parent" | "validFrom" | "validTo" | "status" | "actions";

/**
 * The dates are held as yyyy-MM-dd, which is what the app displays anyway,
 * so they are resolved raw. That also sorts them correctly - the format is
 * lexicographic in the same order it is chronological - which matters
 * because the grid sorts on whatever the resolver returns and offers no
 * comparator of its own.
 *
 * Parents can never claim the same day, so no two stretches share a start
 * and validFrom is a key on its own. It is also how a row is identified
 * when one of its dates is being changed.
 *
 * Every row can have its end moved and every row can be deleted. Where a
 * stretch starts is decided when the parent is added and does not move
 * after. Where a stretch sits against today decides how it is badged and
 * what deleting it is warned to cost, not whether it can be touched.
 */
export const createParentHistoryTableConfig = (
  history: ParentHistoryEntry[],
  today: string,
  /* Both settle once the write and the read after it are done, which is
     what lets the popover that started one hold a spinner until then. */
  onSetEnd: (key: ParentStretchKey, validTo: string) => Promise<unknown>,
  onDelete: (key: ParentStretchKey) => Promise<unknown>
): StandardTableConfig<ParentHistoryEntry, HistoryColumns> => ({
  keyResolver: (entry) => entry.validFrom,
  disableInfiniteList: true,
  initialSortOrder: "validFrom",
  initialSortOrderDesc: true,
  columnOrder: ["parent", "validFrom", "validTo", "status", "actions"],
  columns: {
    parent: {
      /* The index where there is one, so sorting groups by what is shown. */
      itemValueResolver: (entry) => entry.parentIndex ?? entry.parentCustomerNo,
      columnLabel: "Parent",
      minWidth: "160px",
      renderCell: ({ item }) => (
        <Indent overflow={"hidden"}>
          <Text
            whiteSpace={"nowrap"}
            overflow={"hidden"}
            textOverflow={"ellipsis"}
          >
            {/* Index with the number after it, as the app says it elsewhere.
                Where the customer did not resolve, the number is all there
                is to say. */}
            {item.parentIndex
              ? `${item.parentIndex} (${item.parentCustomerNo})`
              : item.parentCustomerNo}
          </Text>
        </Indent>
      ),
    },
    validFrom: {
      /* Set once, when the parent is added, and left alone after. */
      itemValueResolver: (entry) => entry.validFrom,
      columnLabel: "Valid from",
      minWidth: "120px",
    },
    validTo: {
      itemValueResolver: (entry) => entry.validTo ?? "",
      columnLabel: "Valid to",
      minWidth: "170px",
      renderCell: ({ item }) => (
        <ParentHistoryEndDateCell
          entry={item}
          history={history}
          onSetEnd={(validTo) => onSetEnd(stretchKey(item), validTo)}
        />
      ),
    },
    status: {
      /** The state itself, so sorting groups the kinds of row together. */
      itemValueResolver: (entry) => parentStretchState(entry, today),
      columnLabel: "Status",
      minWidth: "110px",
      renderCell: ({ item }) => {
        const { label, variant } = parentStretchTag(
          parentStretchState(item, today)
        );

        return (
          <Indent>
            <Tag label={label} variant={variant} />
          </Indent>
        );
      },
    },
    actions: {
      itemValueResolver: () => "",
      columnLabel: "",
      width: "60px",
      justifyContentCell: "center",
      renderCell: ({ item }) => (
        <DeleteParentButton
          entry={item}
          today={today}
          onDelete={() => onDelete(stretchKey(item))}
        />
      ),
    },
  },
});
